/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-classic-components, ember/no-component-lifecycle-hooks, ember/no-computed-properties-in-native-classes, ember/no-get, ember/no-observers, ember/no-runloop, ember/require-computed-property-dependencies, ember/require-tagless-components, getter-return, warp-drive/no-legacy-request-patterns */
import { tracked } from '@glimmer/tracking';
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import { cancel, debounce } from '@ember/runloop';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import { observes } from '@ember-decorators/object';
import {
  generateLeaverRequestParams,
  validateLeaver,
} from 'linced-app/utils/nz-moe/leaver';
import State from 'linced-app/enums/enrolment-request-state';
import Resource from 'linced-app/enums/enrolment-resource';
import JurisdictionalStatus from 'linced-app/enums/jurisdictional-status';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import { optionAlias } from '@linc-technologies/linced-common/utils/options';

export default class LeaverUpload extends Component {
  // Services
  @service
  fetch;

  @service
  options;

  @service
  paperToaster;

  @service
  session;

  @service
  store;

  @service
  students;

  State = State;

  @optionAlias('nzMOE:schoolType:type')
  schoolType;

  @computed(
    'person.{gender,dob,currentYearLevel,jurisdictionalId}',
    'enrolment',
  )
  get uploadValidationErrors() {
    return validateLeaver(this.person, this.enrolment, this.schoolType);
  }

  @tracked checkingStatus = false;
  leaver = null;
  status = null;

  @computed('uploadValidationErrors.length', 'checkingStatus', 'uploading')
  get canUpload() {
    if (this.checkingStatus || this.uploading) {
      // Status check and/or upload in progress; don't allow upload.
      return false;
    }
    if (this.get('uploadValidationErrors.length') > 0) {
      // Not valid; don't allow upload.
      return false;
    }
  }

  @tracked uploading = false;

  @computed('leaver.nzMoe.type')
  get uploaded() {
    let type = this.get('leaver.nzMoe.type');
    // A `J` (success) or `I` (not enrolled at calling school) response
    // is considered a successful upload.
    return type === 'J' || type === 'I';
  }

  uploadErrors = null;
  @tracked
  detailsUpdating = false;
  @tracked
  detailsUpdated = false;

  @tracked
  pollTimer;

  didReceiveAttrs() {
    // Trigger check for an in-flight upload.
    this.checkStatus();

    return super.didReceiveAttrs(...arguments);
  }

  checkStatus() {
    this.checkingStatus = true;
    let params = {
      jurisdiction: Jurisdiction.NZ_MOE,
      personId: this.person.id,
      personEnrolmentId: this.enrolment.id,
    };
    return this.store
      .query('enrolments/unenrolment', params)
      .then((leavers) => {
        if (!isEmpty(leavers)) {
          // We have at least one Unenrolment (leaver) response.
          let successful =
            findBy(leavers, 'nzMoe.type', 'J') ||
            findBy(leavers, 'nzMoe.type', 'I');
          if (!isEmpty(successful)) {
            // We have a J (success) or I (not enrolled at calling school) response.
            // Set uploaded flag so we don't/can't try again.
            this.setProperties({
              leaver: successful,
            });
            // Since this student is marked as unenrolled in ENROL,
            // update their jurisdictional enrolment status to match.
            return this.markUploaded();
          }
        }

        // No existing "successy" Unenrolment (leaver) records.
        // Check for in-flight Status requests.
        params.status = State.PROCESSING;
        params.resource = Resource.UNENROLMENTS;
        return this.store
          .query('enrolments/status', params)
          .then((statuses) => {
            if (!isEmpty(statuses)) {
              // We got at least one Status.
              // We should usually only have one in-flight Status request at most,
              // so just pick the first one returned.
              this.set('status', statuses[0]);
            }
          });
      })
      .finally(() => {
        this.checkingStatus = false;
        // Update pending upload counts.
        this.students.updatePendingUploads();
      });
  }

  markUploaded() {
    return this.enrolment
      .reload()
      .then((enrolment) => {
        if (
          enrolment.jurisdictionalStatus ===
          JurisdictionalStatus.PENDING_UNENROLMENT
        ) {
          enrolment.set(
            'jurisdictionalStatus',
            JurisdictionalStatus.UNENROLLED,
          );
          return enrolment.save();
        }
        return Promise.resolve();
      })
      .then(() => {
        this.paperToaster.show(
          `${this.person.fullName}'s departure has been uploaded to ENROL successfully.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'success',
          },
        );
      })
      .catch(() => {
        this.paperToaster.show(
          `Error updating ${this.person.fullName}'s ENROL status.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }

  removeNSN() {
    return this.person.reload().then((person) => {
      person.get('fields.nzMOE:nsn').set('value', '');
      person.set('jurisdictionalId', '');
      return person.save();
    });
    // NOTE: No toasts on this, as it's not something which the user requested/expects.
  }

  @observes('status', 'leaver')
  pollStatus() {
    if (!isEmpty(this.status)) {
      if (isEmpty(this.leaver)) {
        // We have a Status (request), but not yet an Unenrolment (leaver) response.
        this.pollTimer = debounce(this, 'updateStatus', 2000, false);
      } else if (!isEmpty(this.pollTimer)) {
        // We've got an Unenrolment (leaver); cancel any pending status poll.
        cancel(this.pollTimer);
        this.pollTimer = null;
      }
    }
  }

  updateStatus() {
    if (isEmpty(this.status)) {
      // If we don't have a Status record, we can't poll it for updates.
      return;
    }
    this.status.reload().then((status) => {
      let resourceId = status.get('resourceIds.firstObject');
      switch (status.status) {
        case State.READY:
          // Processing complete - fetch the Unenrolment (leaver) record.
          if (!isEmpty(resourceId)) {
            this.store
              .findRecord('enrolments/unenrolment', resourceId)
              .then((leaver) => {
                this.setProperties({
                  leaver,
                  uploading: false,
                });
                switch (leaver.get('nzMoe.type')) {
                  case 'J':
                  case 'I':
                    // We got a "successy" response.
                    this.markUploaded();
                    break;

                  case 'G':
                    // NSN not found.
                    this.removeNSN();
                    break;
                }
              });
            // Update pending upload counts.
            this.students.updatePendingUploads();
          }
          break;

        case State.PROCESSING:
          // Still processing - queue another update.
          this.pollTimer = debounce(this, 'updateStatus', 2000, false);
          break;
      }
    });
  }

  @action
  upload() {
    this.uploading = true;
    let params = generateLeaverRequestParams(
      this.person,
      this.enrolment,
      this.session,
    );
    let status = this.store.createRecord('enrolments/status', {
      schoolId: this.session.school,
      requesterId: this.session.person,
      jurisdiction: Jurisdiction.NZ_MOE,
      resource: Resource.UNENROLMENTS,
      nzMoe: [params],
    });
    this.setProperties({
      status,
      leaver: null,
    });
    status.save();
  }

  @action
  updateDetails(differences) {
    this.detailsUpdating = true;
    this.detailsUpdated = false;
    return this.person
      .reload()
      .then((person) => {
        differences.forEach((difference) => {
          person.get(`fields.${difference.key}`).set('value', difference.enrol);
        });
        return person.save();
      })
      .then(() => {
        this.detailsUpdating = false;
        this.detailsUpdated = true;
      })
      .catch(() => {
        this.detailsUpdating = false;
        this.detailsUpdated = false;

        this.paperToaster.show(
          `Error updating ${this.person.fullName}'s details.`,
          {
            duration: 4000,
            position: 'top right',
            toastClass: 'warning',
          },
        );
      });
  }
}
